import { Link } from 'react-router-dom';
import type { IconType } from 'react-icons';
import { cn } from '@/components/ui/utils';

/**
 * Compact wallet / PeliPOINTS pill shown on Home.
 *
 * Renders: [icon] value · label   (e.g. 💳 $12.50 · Wallet)
 *
 * Entire pill is a link to the detail screen. Skeleton state when loading.
 */
export interface MoneyChipProps {
  to: string;
  icon: IconType;
  value: string;
  label: string;
  loading?: boolean;
  tone?: 'brand' | 'cta';
  className?: string;
}

const TONE_STYLES: Record<NonNullable<MoneyChipProps['tone']>, string> = {
  brand: 'bg-[var(--brand-light)] text-[var(--brand)]',
  cta: 'bg-[color-mix(in_oklab,var(--cta)_15%,transparent)] text-[var(--cta)]',
};

export function MoneyChip({
  to,
  icon: Icon,
  value,
  label,
  loading = false,
  tone = 'brand',
  className,
}: MoneyChipProps) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center gap-2 rounded-full pl-2 pr-3 py-1.5 min-h-[36px]',
        'text-[13px] font-medium border border-[var(--border)] bg-[var(--surface)]',
        'hover:bg-[var(--background)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
        className,
      )}
      aria-label={`${label}: ${loading ? 'loading' : value}`}
    >
      <span
        className={cn(
          'inline-flex items-center justify-center w-6 h-6 rounded-full',
          TONE_STYLES[tone],
        )}
        aria-hidden
      >
        <Icon size={14} />
      </span>
      <span className="text-[var(--text)] font-semibold tabular-nums">
        {loading ? '—' : value}
      </span>
      <span className="text-[var(--text-muted)]">·</span>
      <span className="text-[var(--text-light)]">{label}</span>
    </Link>
  );
}
